import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { OKTA_AUTH } from '@okta/okta-angular';
import { Observable, throwError, EMPTY } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AuthService } from '../../../auth/auth.service';

/**
 * Maintenance Mode Interceptor
 *
 * Intercepts HTTP 423 LOCKED responses which indicate the system is under maintenance.
 * When detected, redirects to the maintenance page.
 *
 * The MaintenanceGuard will prevent access to protected routes.
 */
@Injectable()
export class MaintenanceInterceptor implements HttpInterceptor {
  private readonly __router: Router = inject(Router);
  private readonly __authService = inject(AuthService);
  private readonly __oktaAuth = inject(OKTA_AUTH);
  private redirecting = false;

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        // Check if this is a 423 LOCKED response (maintenance mode)
        // Only handle if user is authenticated (has a token)
        if (
          error.status === 423 &&
          !this.redirecting &&
          this.__oktaAuth.getAccessToken()
        ) {
          this.handleMaintenanceLock();
          // Return EMPTY to complete the observable chain without emitting
          return EMPTY;
        }

        // Re-throw the error for other interceptors to handle
        return throwError(() => error);
      }),
    );
  }

  private handleMaintenanceLock(): void {
    // Prevent multiple simultaneous redirects
    if (this.redirecting) {
      return;
    }

    this.redirecting = true;

    // Track for insights
    this.__authService.addLogoutInsights('MaintenanceMode423', {
      timestamp: new Date().toISOString(),
    });

    // Navigate to maintenance page
    // Don't clear user from store - let them stay logged in
    // The guard will prevent access to protected routes
    this.__router
      .navigate(['/maintenance'], { replaceUrl: true })
      .then(() => {
        // Reset flag after navigation completes
        this.redirecting = false;
      })
      .catch((error: unknown) => {
        console.error('[MaintenanceInterceptor] Navigation error:', error);
        // Reset flag even on error
        this.redirecting = false;
      });
  }
}
